import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './ClosedTicketsTicker.css';
import UserBullet from '../../UserBullet';
import { createMarkup } from '../../../helpers/generic';


const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const m = Math.floor(diff / 60000);
    const h = Math.floor(diff / 3600000);
    const d = Math.floor(diff / 86400000);
    if (m < 1)  return 'just now';
    if (m < 60) return `${m}m ago`;
    if (h < 24) return `${h}h ago`;
    if (d < 30) return `${d}d ago`;
    return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const Chip = ({ color, text }) => (
    <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: '0.68rem', fontWeight: 500, padding: '2px 7px',
        borderRadius: 5, background: '#f0f0f0', color: '#333',
        whiteSpace: 'nowrap', border: '1px solid #e0e0e0',
    }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: color, flexShrink: 0 }} />
        {text}
    </span>
);

const TicketCard = ({ ticket }) => (
    <Link to={`/ticket/${ticket.id}`}>
        <div
            className="ctt-card"
            onMouseEnter={e => e.currentTarget.style.borderColor = '#a5b4fc'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#e5e7eb'}
        >
            {/* Title */}
            <div className="ctt-card-title">{ticket.title}</div>

            {/* Description */}
            {ticket.description && (
                <div
                    className="ctt-card-desc"
                    dangerouslySetInnerHTML={createMarkup(ticket.description)}
                />
            )}

            {/* Department chip only — no status, no priority */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {ticket.department && <Chip color={ticket.department.color} text={ticket.department.name} />}
            </div>

            {/* Footer: assignee avatar(s) (name shows on hover via tooltip) + time */}
            <div className="ctt-card-footer">
                {(() => {
                    const assignees = ticket.assignees?.length
                        ? ticket.assignees
                        : ticket.assignee
                            ? [typeof ticket.assignee === 'string'
                                ? { name: ticket.assignee, profile_image: null }
                                : ticket.assignee]
                            : [];
                    if (!assignees.length) {
                        return <span style={{ fontSize: '0.72rem', color: '#d1d5db' }}>Unassigned</span>;
                    }
                    return (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {assignees.slice(0, 4).map((u, idx) => (
                                <div key={idx} style={{ marginLeft: idx === 0 ? 0 : 3, zIndex: assignees.length - idx }}>
                                    <UserBullet
                                        user={typeof u === 'string' ? { name: u, profile_image: null } : u}
                                        size={22}
                                        tooltip={true}
                                    />
                                </div>
                            ))}
                            {assignees.length > 4 && (
                                <span style={{
                                    marginLeft: 3, width: 22, height: 22,
                                    borderRadius: '50%', background: '#e5e7eb',
                                    border: '1.5px solid #fff', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.6rem', fontWeight: 600, color: '#6b7280',
                                }}>
                                    +{assignees.length - 4}
                                </span>
                            )}
                        </div>
                    );
                })()}
                <span style={{ display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M2 5l2.5 2.5 3.5-4" stroke="#22c55e" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{formatTimeAgo(ticket.closed_at)}</span>
                </span>
            </div>
        </div>
    </Link>
);

const SAMPLE_TICKETS = [
    { id: 1, title: 'Login page broken on mobile',       description: 'Users on iOS 17 report a white screen after submitting credentials. Happens only on Safari.', department: { name: 'Support',     color: '#6366f1' }, assignee: 'Alice M.',  closed_at: new Date(Date.now() - 10  * 60000).toISOString() },
    { id: 2, title: 'Invoice not generating correctly',  description: 'PDF invoices are missing line items when there are more than 10 products in the order.',        department: { name: 'Billing',     color: '#f59e0b' }, assignee: 'Bob K.',    closed_at: new Date(Date.now() - 45  * 60000).toISOString() },
    { id: 3, title: 'Email notifications delayed',       description: 'Notification emails are arriving 2–3 hours late. Affects password resets and ticket updates.', department: { name: 'Engineering', color: '#10b981' }, assignee: 'Carol S.',  closed_at: new Date(Date.now() - 2   * 3600000).toISOString() },
    { id: 4, title: 'Dark mode flickering issue',        description: 'Theme flickers between light and dark on page load. Reproducible on Chrome 123 and Firefox.',  department: { name: 'Design',      color: '#ec4899' }, assignee: 'David P.',  closed_at: new Date(Date.now() - 5   * 3600000).toISOString() },
    { id: 5, title: 'CSV export missing columns',        description: 'Exported CSV files do not include the custom fields added in the last release.',               department: { name: 'Support',     color: '#6366f1' }, assignee: 'Elena R.',  closed_at: new Date(Date.now() - 1   * 86400000).toISOString() },
    { id: 6, title: '2FA setup wizard crashes on iOS',   description: 'The setup wizard for two-factor authentication throws an unhandled exception on step 3.',     department: { name: 'Engineering', color: '#10b981' }, assignee: 'Frank T.',  closed_at: new Date(Date.now() - 2   * 86400000).toISOString() },
    { id: 7, title: 'Password reset link expires early', description: 'Reset links are expiring after 5 minutes instead of the configured 60 minutes.',              department: { name: 'Security',    color: '#ef4444' }, assignee: 'Grace L.',  closed_at: new Date(Date.now() - 3   * 86400000).toISOString() },
];

const ClosedTicketsTicker = ({ tickets = [], useSampleData }) => {
    const data = useSampleData ? SAMPLE_TICKETS : tickets;
    const trackRef    = useRef(null);
    const stripRef    = useRef(null);
    const [shouldScroll, setShouldScroll] = useState(false);

    useEffect(() => {
        if (!trackRef.current || !stripRef.current) return;
        const check = () => {
            const trackWidth = trackRef.current?.offsetWidth ?? 0;
            const stripWidth = stripRef.current?.offsetWidth ?? 0;
            setShouldScroll(stripWidth > trackWidth);
        };
        // Two rAF calls: first lets React commit to DOM, second lets browser finish layout/paint
        const raf = requestAnimationFrame(() => requestAnimationFrame(check));
        const ro = new ResizeObserver(check);
        ro.observe(trackRef.current);
        return () => { cancelAnimationFrame(raf); ro.disconnect(); };
    }, [data]);

    if (!data.length) return (
        <div className="ctt-wrapper">
            <span className="ctt-empty">No closed tickets yet.</span>
        </div>
    );

    const LoopDivider = () => (
        <div className="ctt-loop-divider" aria-hidden="true" />
    );

    return (
        <div className="ctt-wrapper">
            <div className="ctt-track" ref={trackRef}>
                <div className={`ctt-strip${shouldScroll ? '' : ' ctt-strip--static'}`}>
                    {/* First set in its own div so offsetWidth is measurable */}
                    <div ref={stripRef} style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
                        {data.map((ticket, i) => (
                            <div key={`a-${ticket.id}-${i}`} className="ctt-card-wrap">
                                <TicketCard ticket={ticket} />
                            </div>
                        ))}
                    </div>
                    {shouldScroll && <LoopDivider />}
                    {shouldScroll && data.map((ticket, i) => (
                        <div key={`b-${ticket.id}-${i}`} className="ctt-card-wrap">
                            <TicketCard ticket={ticket} />
                        </div>
                    ))}
                    {shouldScroll && <LoopDivider />}
                </div>
            </div>
        </div>
    );
};

export default ClosedTicketsTicker;
